import React, { useState, useMemo, useCallback, useEffect } from "react";
import { object } from "prop-types";
import get from "lodash/get";
import { ResponsiveImage } from "@quintype/components";
import { useIsMobile } from "../../hooks/useIsMobile";
import Breadcrumb from "../../molecules/Breadcrumb";
import TeamCard from "../../molecules/TeamCard";
import TeamMemberPopup from "../../molecules/TeamMemberPopup";
import { Icon } from "../../atoms/icon";
import HamburgerSvgIcon from "../../atoms/HamburgerSvgIcon";
import { OUR_TEAM_PAGE_CONTENT } from "./constants";

import "./our-team.m.css";

/* Transforms API entity data to component-friendly format */
const transformMemberData = (entityData) => {
  if (!entityData) return null;

  const photo = get(entityData, "photo[0]", null);
  const imageSlug = photo?.key || null;
  const imageMetadata = photo?.metadata || null;

  return {
    id: entityData.slug || String(entityData.id),
    name: entityData.name || "",
    designation: entityData.designation || entityData.role || "",
    imageSlug,
    imageMetadata,
    image: photo?.url || entityData.image || "/icons/team/placeholder.png",
    linkedIn: entityData["linkedin-url"] || entityData.linkedin || "",
    bio: entityData.description ? [{ text: entityData.description }] : [],
  };
};

/* Fetches team entity and its members from API */
const fetchTeamData = async (entitySlug) => {
  try {
    const teamResponse = await fetch(`/api/v1/entities/${entitySlug}`);
    if (!teamResponse.ok) throw new Error("Failed to fetch team entity");

    const teamEntity = await teamResponse.json();


    const teamMembers = teamEntity?.entity?.["team-members"] || [];

    const memberPromises = teamMembers.map(async (member) => {
      try {
        const memberResponse = await fetch(`/api/v1/entities/${member.slug}`);
        if (!memberResponse.ok) return null;
        const memberData = await memberResponse.json();
        return memberData?.entity || null;
      } catch {
        return null;
      }
    });

    const membersData = await Promise.all(memberPromises);
    return membersData.filter(Boolean);
  } catch (error) {
    console.error(`Error fetching team data for ${entitySlug}:`, error);
    return [];
  }
};

/* OurTeamPage - Team page with 4 responsive breakpoints */
export const OurTeamPage = ({ data }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState("leadership");
  const [teamsCache, setTeamsCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isTablet = useIsMobile(1023);
  const isSmallTablet = useIsMobile(767);

  const pageContent = get(data, "pageContent", {});
  const pageTitle = pageContent.pageTitle || OUR_TEAM_PAGE_CONTENT.PAGE_TITLE;
  const pageDescription = pageContent.pageDescription || OUR_TEAM_PAGE_CONTENT.PAGE_DESCRIPTION;
  const heroBannerImage = pageContent.heroBannerImage || null;

  const teams = useMemo(() => {
    const teamsRaw = pageContent.teams || [];
    return teamsRaw.map(team => ({
      ...team,
      id: team.id || team.slug
    }));
  }, [pageContent.teams]);



  /* Extract leadership team data from server response */
  const leadershipMembers = useMemo(() => {
    const apiMembers = get(data, "teamsData.leadership.members", []);
    if (apiMembers.length > 0) {
      return apiMembers.map(transformMemberData).filter(Boolean);
    }
    return [];
  }, [data]);

  /* Get current team members from cache or server data */
  const currentMembers = useMemo(() => {
    if (activeAnchor === "leadership") {
      return teamsCache.leadership || leadershipMembers;
    }
    return teamsCache[activeAnchor] || [];
  }, [activeAnchor, leadershipMembers, teamsCache]);

  /* Fetches team data for a tab and caches it */
  const loadTeamData = useCallback(async (tabId) => {
    if (teamsCache[tabId]) return;

    const team = teams.find(t => t.id === tabId);
    if (!team) return;

    const entitySlug = team.slug;

    setIsLoading(true);
    const membersData = await fetchTeamData(entitySlug);
    const transformedMembers = membersData.map(transformMemberData).filter(Boolean);

    setTeamsCache((prev) => ({
      ...prev,
      [tabId]: transformedMembers,
    }));
    setIsLoading(false);
  }, [teamsCache, teams, activeAnchor]);

  const handleMemberClick = (member) => {
    if (member.bio?.length > 0) {
      setSelectedMember(member);
    }
  };

  const handleClosePopup = () => {
    setSelectedMember(null);
  };

  /* Handles expand toggle - only one card expanded at a time */
  const handleToggleExpand = (memberId) => {
    setExpandedMemberId(memberId);
  };

  /* Handles tab switch - fetches data if not cached, resets expanded state */
  const handleAnchorClick = async (id) => {
    setActiveAnchor(id);
    setExpandedMemberId(null);
    setIsMobileMenuOpen(false); // Close mobile menu on data selection
    window.location.hash = id;

    if (id !== "leadership" && !teamsCache[id]) {
      await loadTeamData(id);
    }

    setTimeout(() => {
      const section = document.getElementById("team-section-start");
      if (section) {
        const yOffset = isSmallTablet ? -80 : -100;
        const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 200);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "");
      const teamExists = teams.some(t => t.id === hash);

      if (hash && teamExists) {
        setActiveAnchor(hash);
        setExpandedMemberId(null);
      } else if (!hash) {
        setActiveAnchor("leadership");
        setExpandedMemberId(null);
      }
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [teams]);

  useEffect(() => {
    if (activeAnchor !== "leadership" && !teamsCache[activeAnchor]) {
      loadTeamData(activeAnchor);
    }
  }, [activeAnchor, loadTeamData, teamsCache]);

  /* Effect to unstick global header on this page so local nav can be sticky */
  useEffect(() => {
    const header = document.getElementById("sticky-navbar");
    if (header) {
      header.classList.add("unstick-header");
    }

    return () => {
      if (header) {
        header.classList.remove("unstick-header");
      }
    };
  }, []);

  const getCardVariant = () => {
    if (isSmallTablet) return "default";
    if (isTablet) return "tablet";
    return "default";
  };

  /* Renders hero banner image using ResponsiveImage or fallback */
  const renderHeroBannerImage = () => {
    if (heroBannerImage?.key) {
      return (
        <ResponsiveImage
          slug={heroBannerImage.key}
          metadata={heroBannerImage.metadata}
          aspectRatio={[1440, 420]}
          defaultWidth={1440}
          widths={[768, 1024, 1280, 1440]}
          imgParams={{ auto: ["format", "compress"] }}
          eager="above-fold"
          alt={pageTitle}
        />
      );
    }
    return (
      <img
        src={heroBannerImage?.url || "/icons/team/hero-banner.png"}
        alt={pageTitle}
        styleName="hero-image-fallback"
      />
    );
  };

  const renderTeamGrid = (members) => {
    if (isLoading) {
      return <p styleName="loading-text">Loading team members...</p>;
    }

    if (!members || members.length === 0) {
      return <p styleName="no-members">No team members found.</p>;
    }

    if (isSmallTablet) {
      return (
        <div styleName="team-grid mobile">
          {members.map((member) => (
            <TeamCard
              key={member.id}
              member={member}
              onMemberClick={handleMemberClick}
              onToggleExpand={handleToggleExpand}
              isExpanded={expandedMemberId === member.id}
              variant="default"
            />
          ))}
        </div>
      );
    }

    const rows = [];
    for (let i = 0; i < members.length; i += 3) {
      const rowMembers = members.slice(i, i + 3);
      rows.push(
        <div key={i} styleName="team-row">
          {rowMembers.map((member) => (
            <TeamCard
              key={member.id}
              member={member}
              onMemberClick={handleMemberClick}
              onToggleExpand={handleToggleExpand}
              isExpanded={expandedMemberId === member.id}
              variant={getCardVariant()}
            />
          ))}
        </div>
      );
    }
    return <div styleName="team-grid">{rows}</div>;
  };

  /* Formats description with highlighted first word */
  const renderDescription = () => {
    const description = pageDescription || "";
    if (description.startsWith("IMA ")) {
      return (
        <>
          <span styleName="description-highlight">IMA </span>
          {description.replace("IMA ", "")}
        </>
      );
    }
    return description;
  };

  return (
    <div styleName="our-team-page">
      {isSmallTablet && (
        <>
          {isMobileMenuOpen && <div styleName="mobile-nav-overlay" onClick={() => setIsMobileMenuOpen(false)} />}

          <div styleName="mobile-menu-btn" onClick={toggleMobileMenu}>
            <HamburgerSvgIcon color="white" />
          </div>

          {isMobileMenuOpen && (
            <div styleName="mobile-drawer">
              {teams.map((team) => (
                <button
                  key={team.id}
                  styleName={`mobile-drawer-link ${activeAnchor === team.id ? "active" : ""}`}
                  onClick={() => handleAnchorClick(team.id)}
                >
                  {team.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Hero Banner */}
      <section styleName="hero-banner">
        <div styleName="hero-image-wrapper">
          {renderHeroBannerImage()}
        </div>
        <div styleName="hero-content">
          <div styleName="breadcrumb-overlay">
            <Breadcrumb className="breadcrumb" items={OUR_TEAM_PAGE_CONTENT.BREADCRUMBS} />
          </div>
          <h1 styleName="hero-title">{pageTitle}</h1>
        </div>
      </section>

      {/* Anchor Navigation - Desktop and Tablet only (Hidden via CSS on mobile) */}
      <nav styleName="anchor-nav">
        <div styleName="anchor-nav-content">
          <div styleName="anchor-links">
            {teams.map((team) => (
              <button
                key={team.id}
                styleName={`anchor-link ${activeAnchor === team.id ? "active" : ""}`}
                onClick={() => handleAnchorClick(team.id)}
              >
                {team.name}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Description Section */}
      <section styleName="description-section">
        <p styleName="description-text">
          {renderDescription()}
        </p>
      </section>

      {/* Team Section - renders based on active tab */}
      <section styleName="team-section" id={activeAnchor}>
        <div styleName="section-header" id="team-section-start">
          <h2 styleName="section-title">
            {teams.find(t => t.id === activeAnchor)?.name || "Leadership"}
          </h2>
        </div>
        {renderTeamGrid(currentMembers)}
      </section>

      {/* Team Member Popup */}
      <TeamMemberPopup
        member={selectedMember}
        isOpen={!!selectedMember}
        onClose={handleClosePopup}
        isMobile={isSmallTablet}
      />

      {/* Bottom CTA Bar - Mobile only */}
      {/* {isSmallTablet && (
        <div styleName="bottom-cta-bar">
          <button styleName="cta-button">Become a member</button>
        </div>
      )} */}
    </div>
  );
};

OurTeamPage.propTypes = {
  data: object
};

export default OurTeamPage;
